"use client";

import {JSX, useEffect, useState} from "react";

import Image from "next/image";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import {LocatedBlocksCard} from "./parts/located-blocks-card";
import type {LocatedBlocksData, LocatedItem} from "./parts/located-blocks-data";

import "./styles/located-blocks.scss";

export interface LocatedBlocksProps extends LocatedBlocksData {
    space?: SpaceProps;
    anchorId?: string;
}

export default function LocatedBlocks(props: LocatedBlocksProps): JSX.Element {
    const sectionVars = useSectionSpace(props.space);
    const {title, icon, cities, loadBtnText, bottomTitle, bottomText, bottomBtn} = props;

    const isMobile = useMatchMedia({maxWidth: 767});
    const MOBILE_VISIBLE = 5;

    const [visibleCount, setVisibleCount] = useState(MOBILE_VISIBLE);

    useEffect(() => {
        if (isMobile) {
            setVisibleCount(MOBILE_VISIBLE);
        } else {
            setVisibleCount(cities.length);
        }
    }, [isMobile, cities.length]);

    const visibleCities = cities.slice(0, visibleCount);

    return (
        <section className="located-blocks" style={sectionVars}>
            <ContainerBase>
                <div className="located-blocks__header">
                    {icon && (
                        <Image
                            src={assetPath("/images/located-blocks/choose-city-icon.svg")}
                            alt="located icon"
                            width={133}
                            height={117}
                            className="located-blocks__icon"
                        />
                    )}

                    <Typography as="h2" size={{desktop: 32, mobile: 20}} weight={600} className="located-blocks__title">
                        {title}
                    </Typography>
                </div>

                <div className="located-blocks__grid">
                    {visibleCities.map((item: LocatedItem, index: number) => (
                        <LocatedBlocksCard key={index} item={item} />
                    ))}
                </div>

                {isMobile && visibleCount < cities.length && (
                    <div className="located-blocks__load-more-wrap">
                        <ButtonBase
                            variant="secondary-outline"
                            size="xs"
                            className="located-blocks__load-more"
                            onClick={(): void => setVisibleCount((prev) => prev + MOBILE_VISIBLE)}
                        >
                            {loadBtnText}
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M4 6L8 10L12 6" stroke="black" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </ButtonBase>
                    </div>
                )}

                <div className="located-blocks__bottom">
                    <Typography as="h4" size={{desktop: 16, mobile: 16}} weight={500} className="located-blocks__bottom-title">
                        {bottomTitle}
                    </Typography>

                    <Typography as="p" color="paragraph" size={{desktop: 14, mobile: 14}} weight={500} className="located-blocks__bottom-text">
                        {bottomText}
                    </Typography>

                    {bottomBtn && (
                        <ButtonBase
                            className="located-blocks__bottom-link"
                            as="link"
                            href={bottomBtn.link}
                            variant="secondary-outline"
                            size="xs"
                            target={bottomBtn.target || "_self"}
                        >
                            {bottomBtn.text}
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M6 12L10 8L6 4" stroke="black" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </ButtonBase>
                    )}
                </div>
            </ContainerBase>
        </section>
    );
}
